import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  FormControlLabel,
  Switch,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  AccountBalance as AccountBalanceIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { BankAccount, PaymentSettings } from '../../../../types/storeSettings';

interface BankAccountsSectionProps {
  formData: PaymentSettings;
  setFormData: React.Dispatch<React.SetStateAction<PaymentSettings>>;
  editing: boolean;
}

const BankAccountsSection: React.FC<BankAccountsSectionProps> = ({
  formData,
  setFormData,
  editing
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [currentAccount, setCurrentAccount] = useState<BankAccount | null>(null);
  const [accountFormData, setAccountFormData] = useState<BankAccount>({
    id: '',
    bankName: '',
    accountName: '',
    accountNumber: '',
    iban: '',
    isActive: true
  });

  const handleToggleBankAccount = (accountId: string, checked: boolean) => {
    console.log(`Toggling bank account ${accountId} to ${checked}`);
    const updatedAccounts = formData.bankAccounts.map(acc =>
      acc.id === accountId ? { ...acc, isActive: checked } : acc
    );
    setFormData(prev => ({
      ...prev,
      bankAccounts: updatedAccounts
    }));
  };

  const handleOpenDialog = (account?: BankAccount) => {
    if (account) {
      setCurrentAccount(account);
      setAccountFormData(account);
    } else {
      setCurrentAccount(null);
      setAccountFormData({
        id: `bank_${Date.now()}`,
        bankName: '',
        accountName: '',
        accountNumber: '',
        iban: '',
        isActive: true
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  const handleAccountFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setAccountFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSaveAccount = () => {
    if (currentAccount) {
      // تحديث حساب موجود
      const updatedAccounts = formData.bankAccounts.map(acc =>
        acc.id === currentAccount.id ? accountFormData : acc
      );
      setFormData(prev => ({
        ...prev,
        bankAccounts: updatedAccounts
      }));
    } else {
      // إضافة حساب جديد
      setFormData(prev => ({
        ...prev,
        bankAccounts: [...prev.bankAccounts, accountFormData]
      }));
    }
    handleCloseDialog();
  };

  const handleDeleteAccount = (accountId: string) => {
    const updatedAccounts = formData.bankAccounts.filter(acc => acc.id !== accountId);
    setFormData(prev => ({
      ...prev,
      bankAccounts: updatedAccounts
    }));
  };

  return (
    <Box sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="subtitle1" gutterBottom fontWeight="medium">
          الحسابات البنكية
        </Typography>
        {editing && (
          <Button
            startIcon={<AddIcon />}
            variant="outlined"
            size="small"
            onClick={() => handleOpenDialog()}
          >
            إضافة حساب بنكي
          </Button>
        )}
      </Box>
      
      <Paper sx={{ p: 2 }}>
        {formData.bankAccounts.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
            لا توجد حسابات بنكية. قم بإضافة حساب بنكي جديد.
          </Typography>
        ) : (
          <List>
            {formData.bankAccounts.map((account) => (
              <ListItem key={account.id} divider>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <AccountBalanceIcon sx={{ mr: 1, color: 'primary.main' }} />
                      {account.bankName}
                    </Box>
                  }
                  secondary={
                    <>
                      <Typography variant="body2" component="span" display="block">
                        اسم الحساب: {account.accountName}
                      </Typography>
                      <Typography variant="body2" component="span" display="block">
                        رقم الحساب: {account.accountNumber}
                      </Typography>
                      <Typography variant="body2" component="span" display="block">
                        IBAN: {account.iban}
                      </Typography>
                    </>
                  }
                />
                <ListItemSecondaryAction>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {editing && (
                      <>
                        <Tooltip title="تعديل">
                          <IconButton
                            edge="end"
                            onClick={() => handleOpenDialog(account)}
                            size="small"
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="حذف">
                          <IconButton
                            edge="end"
                            onClick={() => handleDeleteAccount(account.id)}
                            size="small"
                            color="error"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                    <FormControlLabel
                      control={
                        <Switch
                          checked={account.isActive}
                          onChange={(e) => handleToggleBankAccount(account.id, e.target.checked)}
                          disabled={!editing}
                          size="small"
                        />
                      }
                      label="مفعل"
                    />
                  </Box>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        )}
      </Paper>

      {/* مربع حوار إضافة/تعديل حساب بنكي */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {currentAccount ? 'تعديل حساب بنكي' : 'إضافة حساب بنكي جديد'}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            name="bankName"
            label="اسم البنك"
            type="text"
            fullWidth
            variant="outlined"
            value={accountFormData.bankName}
            onChange={handleAccountFormChange}
            sx={{ mb: 2, mt: 1 }}
          />
          <TextField
            margin="dense"
            name="accountName"
            label="اسم الحساب"
            type="text"
            fullWidth
            variant="outlined"
            value={accountFormData.accountName}
            onChange={handleAccountFormChange}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            name="accountNumber"
            label="رقم الحساب"
            type="text"
            fullWidth
            variant="outlined"
            value={accountFormData.accountNumber}
            onChange={handleAccountFormChange}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            name="iban"
            label="IBAN"
            type="text"
            fullWidth
            variant="outlined"
            value={accountFormData.iban}
            onChange={handleAccountFormChange}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="inherit">
            إلغاء
          </Button>
          <Button onClick={handleSaveAccount} color="primary" variant="contained">
            حفظ
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BankAccountsSection;
